import { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import useWindowWidth from 'utils/hooks/useWindowWidth'
import { checkPerm } from 'utils/permManager'
import {
  atLeastCampaignsOnlyPlan,
  atLeastIronscalesProtectPlan,
  atLeastStarterPlan,
} from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'

import {
  selectActualBillableAgent,
  selectActualCompanyId,
  selectActualFeatureFlags,
  selectActualIntegrations,
  selectActualLicenses,
  selectActualPermissions,
  selectActualScanBackStatus,
  selectBusinessPillar,
  selectIsActualCompanyDataReady,
  selectIsCustomerAccessRestricted,
  selectIsStaticSalesDemo,
  selectPartner,
  selectRegion,
  selectSelfFeatureFlags,
  selectSelfIntegrations,
  selectSelfLicenses,
  selectSelfPermissions,
} from 'containers/App/selectors'
import { selectIncidentsCount } from 'containers/Polling/selectors'

import { isMobileNavPageAllowed, navConfig as defaultNavConfig } from './navConfig'
import { NAV_HREF_BUILDERS, NAV_PREDICATES, NAV_RELEVANCE } from './navPredicates'

const MOBILE_BREAKPOINT = 1050

const PLAN_GUARDS = {
  starter: atLeastStarterPlan,
  campaignsOnly: atLeastCampaignsOnlyPlan,
  ironscalesProtect: atLeastIronscalesProtectPlan,
}

// Strip a trailing slash so `/foo/` and `/foo` normalize to the same value
// (the app serves both forms). The root path `/` is left alone.
const normalizeTrailingSlash = (s) => (s.length > 1 ? s.replace(/\/$/, '') : s)

// Exact-or-prefix match: `pathname` matches fragment `f` iff they're equal, or
// `pathname` is a path *segment* under `f` (bounded by `/`, not a bare
// substring) — e.g. `/settings/allow-list/foo` matches `/settings/allow-list`,
// but `/settings/allow-list-v2` does not.
const matchesFragment = (pathname, f) => {
  const np = normalizeTrailingSlash(pathname || '')
  const nf = normalizeTrailingSlash(f)
  return np === nf || np.startsWith(`${nf}/`)
}

/**
 * "Not selected while in Settings" rule + path-fragment matching, replicated
 * from `components/pages/navigation/index.js:37-42`.
 *   - if the current pathname is under /settings, nothing is selected (except
 *     the Settings/GroupSettings items, which match /settings explicitly via
 *     their own `match`).
 *   - otherwise, active when pathname exactly equals, or is a path-segment
 *     descendant of, any of the match fragments.
 *
 * pageWithTabs active matching works for free: `match` is the page-root prefix,
 * and the prefix match covers all tab sub-URLs under it.
 *
 * @param {string} pathname
 * @param {string|string[]|undefined} match
 * @returns {boolean}
 */
const isActive = (pathname, match, exact = false) => {
  if (!match) return false
  const fragments = Array.isArray(match) ? match : [match]
  const matchesSettings = fragments.some((f) => f.includes(URL_CONST.settings.self))
  // The not-selected-in-Settings rule applies to items that are NOT themselves
  // settings destinations (Settings/GroupSettings opt back in via their match).
  if (pathname?.includes(URL_CONST.settings.self) && !matchesSettings) {
    return false
  }
  // exact: required when a match is a prefix of sibling routes (e.g. /dashboard
  // vs /dashboard/mitigation) so the parent route isn't active on every child.
  if (exact) {
    const norm = (s) => s.replace(/\/$/, '')
    return fragments.some((f) => norm(pathname || '') === norm(f))
  }
  return fragments.some((f) => matchesFragment(pathname, f))
}

/**
 * Evaluate a standard single-source `requires` block against one entitlement
 * source ({ permissions, licenses, featureFlags, integrations }).
 *
 * Supports: featureFlags (negatable via '!'), permissions (OR via checkPerm),
 * addOns (licenses.add_ons), integrations, atLeastPlan, region.not,
 * partnerPresent. Compound/cross-source rules use `predicateId` instead and are
 * handled by the caller.
 *
 * @param {object} requires
 * @param {object} src - { permissions, licenses, featureFlags, integrations }
 * @param {object} ctx - { region, partner, isStaticSalesDemo }
 * @returns {boolean}
 */
const evalRequires = (requires, src, ctx) => {
  const { featureFlags, permissions, addOns, integrations, atLeastPlan, region, partnerPresent } =
    requires

  if (featureFlags) {
    const ok = featureFlags.every((flag) => {
      if (flag.startsWith('!')) return !src.featureFlags?.[flag.slice(1)]
      return !!src.featureFlags?.[flag]
    })
    if (!ok) return false
  }
  if (permissions && !checkPerm(src.permissions || {}, permissions)) return false
  if (addOns && !addOns.every((a) => src.licenses?.add_ons?.includes(a))) return false
  if (integrations && !integrations.every((i) => src.integrations?.[i])) return false
  if (atLeastPlan && !PLAN_GUARDS[atLeastPlan](src.licenses?.plan)) return false
  if (region && region.not !== undefined && ctx.region === region.not) return false
  if (partnerPresent && !(ctx.partner && !ctx.isStaticSalesDemo)) return false

  return true
}

/**
 * Compute visibility for a single node.
 *
 * @param {object} node
 * @param {object} entitlements - the full dual-source bag (see navPredicates)
 * @returns {boolean}
 */
const isVisible = (node, entitlements) => {
  if (!node.requires) return true

  if (node.requires.predicateId) {
    const predicate = NAV_PREDICATES[node.requires.predicateId]
    if (!predicate) return false
    return !!predicate(entitlements)
  }

  const scope = node.entitlementScope === 'actual' ? entitlements.actual : entitlements.self
  return evalRequires(node.requires, scope, {
    region: entitlements.region,
    partner: entitlements.partner,
    isStaticSalesDemo: entitlements.isStaticSalesDemo,
  })
}

/**
 * Resolve a node's destination, applying a registered href builder when present.
 *
 * @param {object} node
 * @param {object} entitlements
 * @param {(string|number)} companyId
 * @returns {{ path: string|undefined, external: boolean, newTab: boolean }}
 */
const resolveHref = (node, entitlements, companyId) => {
  if (node.href?.hrefBuilderId) {
    const builder = NAV_HREF_BUILDERS[node.href.hrefBuilderId]
    if (builder) return builder(entitlements, { inAppPath: node.path, companyId })
  }
  return {
    path: node.path,
    external: !!node.href?.external,
    newTab: !!node.href?.newTab,
  }
}

/**
 * Decorate one node (and its children) against the entitlement bag + pathname.
 *
 * Locked-vs-hidden model (spec "Lock menu items"):
 *   - `entitled` = the node's `requires` passes (or it has no `requires`).
 *   - `relevant` = the capability applies to this customer's integration type /
 *     prerequisites (NAV_RELEVANCE[node.relevanceId]; no relevanceId -> relevant).
 *   - When NOT entitled:
 *       * `lockable` AND relevant -> VISIBLE but LOCKED (greyed + padlock).
 *       * otherwise               -> HIDDEN (irrelevant, e.g. O365-only
 *         capability on a GWS-only tenant).
 *   - A LOCKED section (its own product is unentitled) stays in the rail but its
 *     children are not independently surfaced (their `visible` is forced false).
 *   - An entitled section with children is visible when it has at least one
 *     visible child (an empty group should not render).
 *
 * @param {object} node
 * @param {object} entitlements
 * @param {string} pathname
 * @param {boolean} isAccessRestricted
 * @param {(string|number)} companyId
 * @returns {object} decorated node
 */
const decorateNode = (
  node,
  entitlements,
  pathname,
  isAccessRestricted,
  companyId,
  hasOpenIncidents = false,
  isMobile = false
) => {
  const entitled = isVisible(node, entitlements)
  // Locked only when the capability is RELEVANT to this customer (integration
  // type / prereqs) but unentitled. Irrelevant or non-lockable -> hidden.
  const relevance = node.relevanceId ? NAV_RELEVANCE[node.relevanceId] : null
  const relevant = relevance ? !!relevance(entitlements) : true
  const locked = !entitled && !!node.lockable && relevant

  const children = node.children
    ? node.children.map((child) =>
        decorateNode(
          child,
          entitlements,
          pathname,
          isAccessRestricted,
          companyId,
          hasOpenIncidents,
          isMobile
        )
      )
    : undefined

  let visible
  if (children) {
    if (entitled) {
      // Entitled section: visible if it has at least one visible child.
      visible = children.some((c) => c.visible)
    } else {
      // Unentitled section: visible (locked) if lockable, otherwise hidden. Its
      // children do not surface independently while the section is locked.
      visible = locked
    }
  } else {
    // Leaf: entitled -> visible; unentitled -> visible only if lockable (locked).
    visible = entitled || locked
  }

  const href = resolveHref(node, entitlements, companyId)

  // Active: a section is active when itself or any descendant is current. A locked
  // section never shows active state (its children are not surfaced).
  const selfActive = isActive(pathname, node.match || node.path, node.exact)
  const active = children
    ? locked
      ? false
      : selfActive || children.some((c) => c.active)
    : selfActive

  return {
    ...node,
    children,
    visible,
    locked,
    active,
    // disabled reproduces today's `disabled={isAccessRestricted}` on links.
    // Nodes flagged `noRestrictDisable` are never disabled by access-restriction.
    disabled:
      (!!isAccessRestricted && !!node.path && !node.noRestrictDisable) ||
      (isMobile && !!node.path && !isMobileNavPageAllowed(node.id)),
    // Bell (spec "Section bell notification"): non-numbered dot driven by REAL
    // open-incident data (open + challenged, selectIncidentsCount). A leaf shows
    // it when bell-eligible (badge:'bell') AND there are open incidents; a
    // section bubbles it up when any visible descendant has it. Counts appear
    // only at the in-page tab level, never in the nav.
    hasBellNotification: children
      ? children.some((c) => c.visible && c.hasBellNotification)
      : node.badge === 'bell' && !!hasOpenIncidents,
    resolvedPath: href.path,
    external: href.external,
    newTab: href.newTab,
  }
}

/**
 * Build the dual-source entitlement bag from the redux store.
 *
 * @returns {import('./navPredicates').NavEntitlements & {
 *   pathname?: string, isAccessRestricted: boolean, companyId: (string|number)
 * }}
 */
export const useNavEntitlements = () => {
  const selfPermissions = useSelector(selectSelfPermissions)
  const selfLicenses = useSelector(selectSelfLicenses)
  const selfFeatureFlags = useSelector(selectSelfFeatureFlags)
  const selfIntegrations = useSelector(selectSelfIntegrations)

  const actualPermissions = useSelector(selectActualPermissions)
  const actualLicenses = useSelector(selectActualLicenses)
  const actualFeatureFlags = useSelector(selectActualFeatureFlags)
  const actualIntegrations = useSelector(selectActualIntegrations)

  const region = useSelector(selectRegion)
  const businessPillar = useSelector(selectBusinessPillar)
  const scanBackStatus = useSelector(selectActualScanBackStatus)
  const partner = useSelector(selectPartner)
  const isStaticSalesDemo = useSelector(selectIsStaticSalesDemo)
  const companyId = useSelector(selectActualCompanyId)
  const isAccessRestricted = useSelector(selectIsCustomerAccessRestricted)
  const actualBillableAgent = useSelector(selectActualBillableAgent)

  return {
    self: {
      permissions: selfPermissions,
      licenses: selfLicenses,
      featureFlags: selfFeatureFlags,
      integrations: selfIntegrations,
    },
    actual: {
      permissions: actualPermissions,
      licenses: actualLicenses,
      featureFlags: actualFeatureFlags,
      integrations: actualIntegrations,
    },
    region,
    businessPillar,
    scanBackStatus: !!scanBackStatus,
    partner: !!partner,
    // Any PSIMA client, including while the selected child is still loading.
    hasPsimaCompanies: !!partner?.has_psima_companies,
    isStaticSalesDemo,
    companyId,
    isAccessRestricted: !!isAccessRestricted,
    actualBillableAgent,
  }
}

/**
 * Resolver hook (§5.2). Reads the dual-source entitlement bag + current
 * pathname, and returns a decorated copy of `config` with per-node
 * `visible` / `locked` / `active` / `disabled` / `hasBellNotification`
 * (plus `resolvedPath` / `external` / `newTab`).
 *
 * Memoized on (selfEntitlements, actualEntitlements, pathname) — a single key
 * so an MSP company switch re-resolves actual-touching nodes while pure-self
 * nodes stay stable (§5.5).
 *
 * @param {object[]} [config=defaultNavConfig]
 * @param {string} [pathname] - current route; falls back to window.location.pathname
 * @returns {object[]} decorated tree
 */
export const useResolvedNav = (config = defaultNavConfig, pathname = undefined) => {
  const entitlements = useNavEntitlements()
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth > 0 && windowWidth <= MOBILE_BREAKPOINT
  // Freeze the last ready tree while an MSP child company's entitlements are
  // still loading. selectActual* falls back to the partner's own (usually
  // broader) entitlements in that window, which briefly shows every section.
  const isCompanyDataReady = useSelector(selectIsActualCompanyDataReady)
  // Real bell signal: open + challenged incidents (same polling source the
  // legacy nav badge used). Drives the non-numbered bell dots.
  const incidentsCount = useSelector(selectIncidentsCount)
  const hasOpenIncidents = !!incidentsCount && incidentsCount > 0
  const currentPath =
    pathname !== undefined
      ? pathname
      : typeof window !== 'undefined'
        ? window.location.pathname
        : ''

  const tree = useMemo(
    () =>
      config.map((node) =>
        decorateNode(
          node,
          entitlements,
          currentPath,
          entitlements.isAccessRestricted,
          entitlements.companyId,
          hasOpenIncidents,
          isMobile
        )
      ),
    // Memo key: the two entitlement sources + pathname (§5.2) + the bell signal.
    // companyId is part of `actual` resolution so it is covered by
    // entitlements.actual identity.
    [config, entitlements, currentPath, hasOpenIncidents, isMobile]
  )

  const lastReadyTreeRef = useRef(null)
  if (isCompanyDataReady) {
    lastReadyTreeRef.current = tree
  }

  return isCompanyDataReady || lastReadyTreeRef.current == null ? tree : lastReadyTreeRef.current
}

// Exposed for unit tests (parity harness reproduces these without the hook).
export { decorateNode, isActive, isVisible, evalRequires, resolveHref }

export default useResolvedNav
